import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useKmsService } from "../providers/kms-service-provider";
import { showToast } from "../components/shared/feedback/custom-toast-shortcut";
import { AdresseFavorite } from "../types/models/adresse-favorite";
import { Adresse } from "../types/models";

/**
 * Adresses favorites d'un utilisateur : chargement initial, ajout et
 * rechargement après ajout.
 */
export const useAdressesFavorites = (idUtilisateur: number | undefined) => {
  const t = useTranslations("save_favorite_drawer");
  const { adressesFavoritesService } = useKmsService();

  const [favorites, setFavorites] = useState<AdresseFavorite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadFavorites = useCallback(async () => {
    if (!idUtilisateur) return;

    setIsLoading(true);
    try {
      setFavorites(await adressesFavoritesService.getFavorites(idUtilisateur));
    } catch (error) {
      showToast.errorApi(error);
    } finally {
      setIsLoading(false);
    }
  }, [adressesFavoritesService, idUtilisateur]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  /** Renvoie `true` si le favori a bien été enregistré. */
  const saveFavorite = useCallback(
    async (nom: string, adresse: Adresse) => {
      if (!idUtilisateur) return false;

      setIsSaving(true);
      try {
        await adressesFavoritesService.addFavorite(idUtilisateur, {
          nom,
          adresse: {
            rue: adresse.rue,
            numero: adresse.numero,
            boite: adresse.boite,
            codePostal: adresse.codePostal,
            ville: adresse.ville,
            pays: adresse.pays,
            placeId: adresse.placeId,
          },
        });
        showToast.success(t("save_success", { name: nom }));
        await loadFavorites();

        return true;
      } catch (error) {
        showToast.errorApi(error);

        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [adressesFavoritesService, idUtilisateur, loadFavorites, t],
  );

  return { favorites, isLoading, isSaving, saveFavorite };
};
